import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, X, Clock, AlertCircle, User, MessageSquare, FileText,
  CheckCircle, DollarSign, MapPin, Calendar, Phone, Camera, Video, XCircle, Star
} from 'lucide-react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherDiscount, setVoucherDiscount] = useState(null);
  const [voucherError, setVoucherError] = useState('');
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);


  const fetchJobDetails = useCallback(async () => {
    try {
      console.log('🔍 Frontend: Fetching job details for ID:', jobId);
      const response = await api.get(`/jobs/${jobId}`);
      console.log('📋 Frontend: Job response received:', response);
      console.log('💰 Frontend: Job payment object:', response.job?.payment);
      console.log('🔄 Frontend: Job status:', response.job?.status);
      setJob(response.job);
    } catch (error) {
      console.error('❌ Frontend: Failed to fetch job details:', error);
      toast.error('Failed to load job details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  // Refetch data when URL changes (e.g., after payment redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const timestamp = urlParams.get('t');
    if (timestamp) {
      fetchJobDetails();
    }
  }, [fetchJobDetails, location.search]);

  const updatePaymentStatus = useCallback(async (reference) => {
    try {
      // Check if job is available
      if (!job || !job._id) {
        toast.error('Job data not loaded. Please refresh the page.');
        return;
      }
      
      // First try with the reference
      let response;
      if (reference) {
        try {
          response = await api.post('/hitpay/update-payment-status', {
            reference: reference,
            status: 'completed'
          });
        } catch (error) {
          // If reference fails, try updating by job ID
          response = await api.post('/hitpay/update-job-payment-status', {
            jobId: job._id,
            status: 'completed'
          });
        }
      } else {
        // No reference available, use job ID directly
        response = await api.post('/hitpay/update-job-payment-status', {
          jobId: job._id,
          status: 'completed'
        });
      }
      
      if (response?.success) {
        toast.success('Payment completed successfully!');
        // Refresh job data to get updated status
        setTimeout(() => {
          fetchJobDetails();
        }, 1000);
      } else {
        toast.error('Payment update failed. Please try again.');
      }
    } catch (error) {
      toast.error(`Failed to update payment status: ${error.message}`);
    }
  }, [fetchJobDetails, job]);

  // Handle payment success redirect
  useEffect(() => {
    // Only process payment success if job data is loaded
    if (!job) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment'); // Check for 'payment=success'
    const status = urlParams.get('status'); // Also check for 'status=completed'
    const reference = urlParams.get('reference');
    
    // Check for both possible success indicators
    if ((paymentStatus === 'success' || status === 'completed') && reference) {
      updatePaymentStatus(reference);
      // Clean up URL
      const url = new URL(window.location);
      url.search = '';
      window.history.replaceState({}, '', url);
    }
  }, [updatePaymentStatus, job]);

  const handleQuoteResponse = async (jobId, response) => {
    try {
      await api.customer.respondToQuote(jobId, { response });
      
      // Update the job status locally
      setJob(prev => ({ ...prev, status: response }));
      
      if (response === 'QUOTE_ACCEPTED') {
        toast.success('Quote accepted! Payment is required before the vendor can start work.');
      } else {
        toast.success('Quote rejected. You can discuss with the vendor for a new quote.');
      }
      
    } catch (error) {
      console.error('Error responding to quote:', error);
      toast.error(`Failed to ${response === 'QUOTE_ACCEPTED' ? 'accept' : 'reject'} quote: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleVoucherValidation = async () => {
    if (!voucherCode.trim()) {
      setVoucherError('Please enter a voucher code');
      return;
    }

    setValidatingVoucher(true);
    setVoucherError('');

    try {
      const response = await api.post('/vouchers/validate', {
        code: voucherCode.trim().toUpperCase(),
        orderAmount: job.totalAmount,
        category: 'ALL'
      });

      if (response.success) {
        setVoucherDiscount(response.data);
        toast.success(`Voucher applied! You save $${response.data.discountAmount}`);
      }
    } catch (error) {
      setVoucherError(error.response?.data?.message || 'Invalid voucher code');
      setVoucherDiscount(null);
      toast.error(error.response?.data?.message || 'Invalid voucher code');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const handleRemoveVoucher = () => {
    setVoucherCode('');
    setVoucherDiscount(null);
    setVoucherError('');
  };

  const finalAmount = voucherDiscount ? voucherDiscount.finalAmount : job?.totalAmount;

  const handlePayment = async () => {
    try {
      setLoading(true);

      const response = await api.post('/hitpay/job-payment', {
        jobId: job._id,
        amount: finalAmount,
        description: `Payment for job: ${job.description || 'Property maintenance service'}`,
        voucherCode: voucherDiscount ? voucherCode.trim().toUpperCase() : undefined
      });

      // The response data might be directly on response or in response.data
      const responseData = response.data || response;

      if (responseData && responseData.payment_url) {
        // Redirect to HitPay payment page
        window.location.href = responseData.payment_url;
      } else {
        toast.error('Payment URL not received from server');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  // Check if order can be cancelled
  const canCancelOrder = () => {
    // Cannot cancel if completed, cancelled, or in progress
    const nonCancellableStatuses = ['COMPLETED', 'CANCELLED', 'IN_PROGRESS'];
    return !nonCancellableStatuses.includes(job?.status);
  };

  // Calculate cancellation fee based on time remaining
  const calculateCancellationFee = () => {
    if (!job?.requestedTimeSlot?.date) {
      return { canCancel: true, fee: 0, refundPercentage: 100, message: 'Full refund minus platform fee' };
    }

    const scheduledDate = new Date(job.requestedTimeSlot.date);
    const now = new Date();
    const hoursUntilService = (scheduledDate - now) / (1000 * 60 * 60);

    if (hoursUntilService >= 48) {
      return { canCancel: true, fee: 0, refundPercentage: 100, message: 'Full refund minus platform fee' };
    } else if (hoursUntilService >= 24) {
      const cancellationFee = (job.totalAmount || 0) * 0.30;
      return { canCancel: true, fee: cancellationFee, refundPercentage: 70, message: '30% cancellation fee applies' };
    } else {
      return { canCancel: false, fee: job.totalAmount || 0, refundPercentage: 0, message: 'No refund - less than 24 hours before service' };
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setCancelling(true);
    try {
      const response = await api.patch(`/jobs/${job._id}/cancel`, {
        reason: cancelReason
      });

      toast.success('Order cancelled successfully');
      setShowCancelModal(false);
      setCancelReason('');

      // Refresh job details
      fetchJobDetails();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  // Mark job as complete
  const handleMarkComplete = async () => {
    const confirmComplete = window.confirm(
      'Are you sure you want to mark this job as complete? This action confirms that the work has been completed to your satisfaction.'
    );

    if (!confirmComplete) return;

    try {
      const response = await api.patch(`/jobs/${job._id}/complete`);
      toast.success('Job marked as complete successfully!');

      // Refresh job details
      fetchJobDetails();
    } catch (error) {
      console.error('Error marking job as complete:', error);
      toast.error(error.response?.data?.message || 'Failed to mark job as complete');
    }
  };

  // Check if job can be marked as complete
  const canMarkComplete = () => {
    const completableStatuses = ['PAID', 'IN_PROGRESS', 'QUOTE_ACCEPTED'];
    return completableStatuses.includes(job?.status);
  };

  // Verify/Approve order (for PENDING_VERIFICATION status)
  const handleVerifyOrder = async () => {
    const confirmVerify = window.confirm(
      'Are you sure you want to approve this order? This confirms that the work has been completed satisfactorily and the vendor will receive payment.'
    );

    if (!confirmVerify) return;

    try {
      const response = await api.patch(`/jobs/${job._id}/verify`);
      toast.success('Order verified and approved successfully!');

      // Refresh job details
      fetchJobDetails();
    } catch (error) {
      console.error('Error verifying order:', error);
      toast.error(error.response?.data?.message || 'Failed to verify order');
    }
  };

  // Check if order needs verification
  const needsVerification = () => {
    return job?.status === 'PENDING_VERIFICATION';
  };

  // Check if job can be rated
  const canRateJob = () => {
    // Show button for completed jobs with vendors
    return job?.status === 'COMPLETED' && job?.vendorId;
  };

  // Navigate to rating page
  const handleRateOrder = () => {
    navigate(`/rate-vendor/${job._id}`);
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      assigned: User,
      in_discussion: MessageSquare,
      quote_sent: FileText,
      quote_accepted: CheckCircle,
      quote_rejected: X,
      paid: DollarSign,
      in_progress: Clock,
      completed: CheckCircle,
      cancelled: X
    };
    return icons[status.toLowerCase()] || Clock;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'ASSIGNED': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'IN_DISCUSSION': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'QUOTE_SENT': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'QUOTE_ACCEPTED': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'QUOTE_REJECTED': return 'bg-red-50 text-red-700 border-red-200';
      case 'PAID': return 'bg-green-50 text-green-700 border-green-200';
      case 'IN_PROGRESS': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'COMPLETED': return 'bg-green-50 text-green-700 border-green-200';
      case 'CANCELLED': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'PENDING': return 'Waiting for admin review';
      case 'ASSIGNED': return 'Assigned to vendor';
      case 'IN_DISCUSSION': return 'Discussing details with vendor';
      case 'QUOTE_SENT': return 'Quote sent for approval';
      case 'QUOTE_ACCEPTED': return 'Quote accepted, payment required before work begins';
      case 'QUOTE_REJECTED': return 'Quote rejected, discuss new quote with vendor';
      case 'PAID': return 'Payment completed, work scheduled';
      case 'IN_PROGRESS': return 'Work in progress';
      case 'COMPLETED': return 'Work completed successfully';
      case 'CANCELLED': return 'Job cancelled';
      default: return 'Unknown status';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="text-center bg-white p-8 rounded-xl shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Job not found</h2>
          <p className="text-gray-600 mb-4">The job you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(job.status);

  return (
    <div className="min-h-screen pt-0" style={{ backgroundColor: '#F9FAFB' }}>
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-orange-100 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
            <div className="flex items-center space-x-3">
              <StatusIcon className="w-6 h-6 text-orange-100" />
              <div>
                <h1 className="text-2xl font-bold text-white">{job.title}</h1>
                <p className="text-orange-100">#{job.jobNumber}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`px-4 py-2 rounded-full text-sm font-medium border bg-white ${getStatusColor(job.status).replace('bg-', 'bg-white ')}`}>
                {job.status.replace('_', ' ').toUpperCase()}
              </div>
              {needsVerification() && (
                <button
                  onClick={handleVerifyOrder}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  Verify & Approve
                </button>
              )}
              {canMarkComplete() && (
                <button
                  onClick={handleMarkComplete}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as Complete
                </button>
              )}
              {canRateJob() && (
                <button
                  onClick={handleRateOrder}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <Star className="w-4 h-4" />
                  Rate Order
                </button>
              )}
              {canCancelOrder() && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Banner */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-full ${getStatusColor(job.status)}`}>
                <StatusIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-xl text-gray-900">{job.status.replace('_', ' ')}</p>
                <p className="text-gray-600">{getStatusDescription(job.status)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-orange-600">
                ${job.totalAmount || job.vendorQuote?.amount || 'TBD'}
              </p>
              <p className="text-sm text-gray-500">Total Amount</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Job Details */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-orange-600" />
                Job Details
              </h3>
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">Description</label>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900">{job.description || 'No description provided'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-2">Category</label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-900 capitalize">{job.category}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-2">Created Date</label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-900">{new Date(job.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                {job.estimatedBudget && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-2">Your Estimated Budget</label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-900">
                        ${job.estimatedBudget} <span className="text-gray-500 text-sm">(for reference only)</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Service Location */}
            {job.location && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-orange-600" />
                  Service Location
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-gray-900 font-medium">{job.location.address}</p>
                      <p className="text-gray-600">
                        {job.location.city}
                        {job.location.state && `, ${job.location.state}`}
                        {job.location.zipCode && ` ${job.location.zipCode}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Scheduled Time */}
            {job.requestedTimeSlot && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                  Scheduled Time
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-900 font-medium">
                        {new Date(job.requestedTimeSlot.date).toLocaleDateString()}
                      </p>
                      {job.requestedTimeSlot.startTime && job.requestedTimeSlot.endTime && (
                        <p className="text-gray-600">
                          {job.requestedTimeSlot.startTime} - {job.requestedTimeSlot.endTime}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Uploaded Files */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Camera className="w-5 h-5 mr-2 text-orange-600" />
                Uploaded Files
                <span className="text-sm text-gray-500 ml-2 font-normal">
                  (Images: {job.images?.length || 0}, Videos: {job.videos?.length || 0})
                </span>
              </h3>
              
              {/* Images */}
              {job.images && job.images.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <Camera className="w-4 h-4 mr-1" />
                    Photos ({job.images.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {job.images.map((imageName, index) => {
                      // Remove /api from URL for uploads
                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                      return (
                      <div key={index} className="relative group">
                        <img
                          src={`${baseUrl}/uploads/${imageName}`}
                          alt={`Order attachment ${index + 1}`}
                          className="w-full h-28 object-cover rounded-xl border border-gray-200 cursor-pointer hover:opacity-80 transition-all hover:shadow-md"
                          onClick={() => handleImageClick(`${baseUrl}/uploads/${imageName}`)}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiA5VjEzTTEyIDE3SDEyLjAxTTIxIDEyQzIxIDE2Ljk3MDYgMTYuOTcwNiAyMSAxMiAyMUM3LjAyOTQ0IDIxIDMgMTYuOTcwNiAzIDEyQzMgNy4wMjk0NCA3LjAyOTQ0IDMgMTIgM0MxNi45NzA2IDMgMjEgNy4wMjk0NCAyMSAxMloiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+';
                            e.target.className = 'w-full h-28 object-contain rounded-xl border border-gray-200 bg-gray-50';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-xl flex items-center justify-center">
                          <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                            Click to view full size
                          </span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Videos */}
              {job.videos && job.videos.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <Video className="w-4 h-4 mr-1" />
                    Videos ({job.videos.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {job.videos.map((videoName, index) => {
                      // Remove /api from URL for uploads
                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                      return (
                      <div key={index} className="relative">
                        <video
                          src={`${baseUrl}/uploads/${videoName}`}
                          className="w-full h-36 object-cover rounded-xl border border-gray-200"
                          controls
                          preload="metadata"
                        />
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded-lg">
                          {videoName}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No files message */}
              {(!job.images || job.images.length === 0) && (!job.videos || job.videos.length === 0) && (
                <div className="text-center py-12">
                  <div className="text-gray-300 mb-3">
                    <Camera className="w-16 h-16 mx-auto" />
                  </div>
                  <p className="text-gray-500">No files were uploaded with this order</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Assigned Vendor */}
            {job.vendorId ? (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <User className="w-5 h-5 mr-2 text-orange-600" />
                  Assigned Vendor
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                      {job.vendorId.firstName?.[0] || 'V'}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-lg">
                        {job.vendorId.firstName} {job.vendorId.lastName}
                      </p>
                      {job.vendorId.email && (
                        <p className="text-gray-600 text-sm mt-1">{job.vendorId.email}</p>
                      )}
                      {job.vendorId.phone && (
                        <div className="flex items-center mt-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4 mr-1" />
                          {job.vendorId.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <User className="w-5 h-5 mr-2 text-orange-600" />
                  Vendor Assignment
                </h3>
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center text-amber-700">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    <span>No vendor assigned yet</span>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing & Quote */}
            {(job.totalAmount || job.status === 'QUOTE_SENT' || job.status === 'QUOTE_ACCEPTED') && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-orange-600" />
                  Pricing & Quote
                </h3>
                
                {job.totalAmount ? (
                  <div className="space-y-6">
                    {/* Quote Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-2">Vendor Quote</label>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-900">
                            Quote submitted by {job.vendorId?.firstName} {job.vendorId?.lastName}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-orange-600">${job.totalAmount.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">Quote Amount</p>
                      </div>
                    </div>

                    {/* Quote Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-2">Quote Amount</label>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-lg font-semibold text-gray-900">${job.totalAmount.toLocaleString()}</p>
                        </div>
                      </div>
                      {job.estimatedBudget && (
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-2">Your Budget</label>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-lg font-semibold text-gray-900">${job.estimatedBudget.toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-2">Quote Status</label>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            job.status === 'QUOTE_ACCEPTED' ? 'text-green-800' :
                            job.status === 'QUOTE_SENT' ? 'text-blue-800' :
                            job.status === 'QUOTE_REJECTED' ? 'text-red-800' :
                            job.status === 'PAID' ? 'text-green-800' :
                            job.status === 'IN_PROGRESS' ? 'text-blue-800' :
                            job.status === 'COMPLETED' ? 'text-green-800' :
                            'text-gray-800'
                          }`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Budget Comparison */}
                    {job.estimatedBudget && job.totalAmount && (
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-2">Budget Comparison</label>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-900">Difference from your budget:</span>
                            <span className={`font-semibold ${
                              job.totalAmount <= job.estimatedBudget ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {job.totalAmount <= job.estimatedBudget ? '-' : '+'}$
                              {Math.abs(job.totalAmount - job.estimatedBudget).toLocaleString()}
                              {job.totalAmount <= job.estimatedBudget ? ' (Under budget)' : ' (Over budget)'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quote Timeline */}
                    {job.statusHistory && job.statusHistory.some(status => ['QUOTE_SENT', 'QUOTE_ACCEPTED', 'QUOTE_REJECTED'].includes(status.status)) && (
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-2">Quote Timeline</label>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="space-y-2">
                            {job.statusHistory
                              .filter(status => ['QUOTE_SENT', 'QUOTE_ACCEPTED', 'QUOTE_REJECTED'].includes(status.status))
                              .map((status, index) => (
                                <div key={index} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-900">
                                    {status.status === 'QUOTE_SENT' ? 'Quote submitted' :
                                     status.status === 'QUOTE_ACCEPTED' ? 'Quote accepted' :
                                     'Quote rejected'}
                                  </span>
                                  <span className="text-gray-500">
                                    {new Date(status.timestamp).toLocaleDateString()} at {new Date(status.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {job.status === 'QUOTE_SENT' && (
                      <div className="mt-4">
                        <p className="text-sm text-orange-600 mb-4">
                          ⏳ Waiting for your approval of this quote
                        </p>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleQuoteResponse(job._id, 'QUOTE_ACCEPTED')}
                            className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 text-sm font-medium shadow-md"
                          >
                            ✅ Accept Quote
                          </button>
                          <button
                            onClick={() => handleQuoteResponse(job._id, 'QUOTE_REJECTED')}
                            className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                          >
                            ❌ Reject Quote
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {job.status === 'QUOTE_ACCEPTED' && (
                      <div className="mt-4 space-y-3">
                        <p className="text-sm text-green-600">
                          ✅ Quote accepted - Payment required before work can begin
                        </p>
                        
                        {job.payment && job.payment.status === 'PAID' && (
                          <div className="border border-gray-200 rounded-lg p-4">
                            <p className="text-green-700 font-medium flex items-center">
                              ✅ Payment Completed
                            </p>
                            <p className="text-green-600 text-sm mt-1">
                              Work can now begin. The vendor has been notified.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {job.status === 'QUOTE_REJECTED' && (
                      <p className="text-sm text-red-600 mt-2">
                        ❌ Quote rejected - You can discuss a new quote with the vendor
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Vendor Quote:</span>
                      <span className="text-gray-500">Pending</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      The vendor is reviewing your request and will provide a quote soon.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Section - Only show after quote is accepted */}
            {job.totalAmount && job.totalAmount > 0 && job.status === 'QUOTE_ACCEPTED' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-orange-600" />
                  Payment
                </h3>
                
                <div className="bg-white rounded-lg p-4 border border-orange-300">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-orange-800 font-medium">Quote Amount:</span>
                    <span className="text-2xl font-bold text-orange-600">${job.totalAmount.toLocaleString()}</span>
                  </div>

                  {(!job.payment || job.payment.status !== 'PAID') ? (
                    <>
                      {/* Voucher Section */}
                      <div className="mb-4 pb-4 border-b border-orange-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Have a voucher code?
                        </label>
                        {!voucherDiscount ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Enter voucher code"
                              value={voucherCode}
                              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-sm"
                              disabled={validatingVoucher}
                            />
                            <button
                              type="button"
                              onClick={handleVoucherValidation}
                              disabled={validatingVoucher || !voucherCode.trim()}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              {validatingVoucher ? 'Validating...' : 'Apply'}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                  <span className="font-medium text-green-800">{voucherDiscount.code}</span>
                                </div>
                                <p className="text-sm text-green-700 mt-1">
                                  {voucherDiscount.description}
                                </p>
                                <p className="text-sm font-semibold text-green-800 mt-1">
                                  You save: ${voucherDiscount.discountAmount}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={handleRemoveVoucher}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        )}
                        {voucherError && (
                          <p className="text-sm text-red-600 mt-1">{voucherError}</p>
                        )}
                      </div>

                      {/* Price Summary */}
                      {voucherDiscount && (
                        <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Original Amount:</span>
                              <span className="text-gray-600">${job.totalAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-green-600">Discount:</span>
                              <span className="text-green-600">-${voucherDiscount.discountAmount}</span>
                            </div>
                            <div className="border-t border-orange-300 pt-2 flex justify-between">
                              <span className="font-bold text-gray-900">Total to Pay:</span>
                              <span className="font-bold text-orange-600 text-xl">${finalAmount.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={handlePayment}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-5 h-5 mr-2" />
                            Pay Now with HitPay
                          </>
                        )}
                      </button>
                      <p className="text-xs text-orange-600 mt-2 text-center">
                        Secure payment powered by HitPay
                      </p>
                    </>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="text-green-700 font-medium flex items-center">
                        ✅ Payment Completed
                      </p>
                      <p className="text-green-600 text-sm mt-1">
                        Payment has been processed successfully.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Information - Only show after payment is completed */}
            {job.payment && (job.payment.status === 'PAID' || job.status === 'PAID') && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-orange-600" />
                  Payment Information
                </h3>
                
                {/* Payment Summary */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600 block mb-2">Payment Status</label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            job.payment.status === 'PAID' ? 'text-green-800' : 
                            job.payment.status === 'FAILED' ? 'text-red-800' : 
                            job.payment.status === 'PENDING' ? 'text-yellow-800' :
                            'text-blue-800'
                          }`}>
                            {job.payment.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-orange-600">
                        ${Number(job.payment.paidAmount || job.totalAmount || 0).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">Amount Paid</p>
                    </div>
                  </div>
                </div>

                {/* Payment Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Transaction Details */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-2">Transaction Details</label>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {/* Generated Reference Number */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Reference Number</span>
                        <span className="text-gray-900 text-sm font-mono bg-white px-2 py-1 rounded border">
                          {job.payment.reference || `REF-${job.jobNumber || job._id?.slice(-6).toUpperCase()}-${new Date(job.payment.paidAt || job.payment.createdAt || Date.now()).getFullYear()}`}
                        </span>
                      </div>
                      
                      {/* Transaction ID */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Transaction ID</span>
                        <span className="text-gray-900 text-sm font-mono bg-white px-2 py-1 rounded border">
                          {job.payment.transactionId || job.payment.paymentId || `TXN-${Date.now().toString().slice(-8)}`}
                        </span>
                      </div>

                      {/* Payment Gateway */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Payment Gateway</span>
                        <span className="text-gray-900 text-sm">
                          {job.payment.gateway || 'HitPay'}
                        </span>
                      </div>

                      {/* Payment Method */}
                      {job.payment.method && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 text-sm">Payment Method</span>
                          <span className="text-gray-900 text-sm capitalize">
                            {job.payment.method}
                          </span>
                        </div>
                      )}

                      {/* Currency */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Currency</span>
                        <span className="text-gray-900 text-sm">
                          {job.payment.currency || 'SGD'}
                        </span>
                      </div>

                      {/* Processing Fee */}
                      {job.payment.processingFee && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 text-sm">Processing Fee</span>
                          <span className="text-gray-900 text-sm">
                            ${Number(job.payment.processingFee).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Net Amount */}
                      <div className="flex justify-between items-center border-t pt-2">
                        <span className="text-gray-600 text-sm font-medium">Net Amount</span>
                        <span className="text-gray-900 text-sm font-semibold">
                          ${Number(job.payment.paidAmount || job.totalAmount || 0).toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Show message if minimal data */}
                      {!job.payment.paymentId && !job.payment.reference && !job.payment.transactionId && !job.payment.method && (
                        <div className="text-sm text-gray-500 italic border-t pt-2">
                          Limited transaction details available - showing generated reference
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Timeline */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-2">Payment Timeline</label>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {/* Payment Request Created */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Payment Request</span>
                        <span className="text-gray-900 text-sm">
                          {job.payment.createdAt ? 
                            `${new Date(job.payment.createdAt).toLocaleDateString()} at ${new Date(job.payment.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` :
                            'Generated automatically'
                          }
                        </span>
                      </div>

                      {/* Payment Completed */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Payment Completed</span>
                        <span className="text-gray-900 text-sm">
                          {job.payment.paidAt ? 
                            `${new Date(job.payment.paidAt).toLocaleDateString()} at ${new Date(job.payment.paidAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` :
                            new Date().toLocaleDateString() + ' at ' + new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                          }
                        </span>
                      </div>

                      {/* Processing Time */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Processing Time</span>
                        <span className="text-gray-900 text-sm">
                          {job.payment.createdAt && job.payment.paidAt ? 
                            `${Math.round((new Date(job.payment.paidAt) - new Date(job.payment.createdAt)) / 1000 / 60)} minutes` :
                            'Instant'
                          }
                        </span>
                      </div>

                      {/* Payment Status */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Status</span>
                        <span className={`text-sm font-medium ${
                          job.payment.status === 'PAID' ? 'text-green-600' : 
                          job.payment.status === 'FAILED' ? 'text-red-600' : 
                          'text-yellow-600'
                        }`}>
                          {job.payment.status || 'COMPLETED'}
                        </span>
                      </div>

                      {/* Amount Breakdown */}
                      <div className="border-t pt-2 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 text-sm">Service Amount</span>
                          <span className="text-gray-900 text-sm">
                            ${Number(job.totalAmount || 0).toFixed(2)}
                          </span>
                        </div>
                        {job.payment.processingFee && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 text-sm">Processing Fee</span>
                            <span className="text-gray-900 text-sm">
                              ${Number(job.payment.processingFee).toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center font-medium">
                          <span className="text-gray-700 text-sm">Total Paid</span>
                          <span className="text-gray-900 text-sm">
                            ${Number(job.payment.paidAmount || job.totalAmount || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Show message if no timeline details */}
                      {!job.payment.createdAt && !job.payment.paidAt && !job.payment.paidAmount && (
                        <div className="text-sm text-gray-500 italic border-t pt-2">
                          Payment timeline showing estimated information
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Work Progress Section */}
        {job.workProgress && job.workProgress.percentage > 0 && (
          <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-600" />
              Work Progress
            </h3>
            <div className="space-y-6">
              {/* Progress Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Completion</span>
                  <span className="text-sm font-bold text-orange-600">{job.workProgress.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${job.workProgress.percentage}%` }}
                  >
                    {job.workProgress.percentage > 15 && (
                      <span className="text-xs font-bold text-white">{job.workProgress.percentage}%</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Work Notes */}
              {job.workProgress.workNotes && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Latest Update</label>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900">{job.workProgress.workNotes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status History */}
        {job.statusHistory && job.statusHistory.length > 0 && (
          <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-600" />
              Status History
            </h3>
            <div className="space-y-4">
              {job.statusHistory.slice().reverse().map((status, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">
                        {status.status.replace('_', ' ')}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(status.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    {status.notes && (
                      <p className="text-sm text-gray-600 mt-2">{status.notes}</p>
                    )}
                    {status.media && status.media.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {status.media.map((mediaFile, mediaIndex) => {
                          const isVideo = mediaFile.match(/\.(mp4|webm|ogg|mov)$/i);
                          // Remove /api from URL for uploads
                          const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                          // Media files are stored in order-attachments subdirectory
                          const mediaUrl = `${baseUrl}/uploads/order-attachments/${mediaFile}`;
                          return (
                            <div key={mediaIndex} className="relative group">
                              {isVideo ? (
                                <video
                                  src={mediaUrl}
                                  className="w-full h-20 object-cover rounded-lg border border-gray-200"
                                  controls
                                />
                              ) : (
                                <img
                                  src={mediaUrl}
                                  alt={`Status update ${mediaIndex + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-all bg-gray-50"
                                  onClick={() => handleImageClick(mediaUrl)}
                                  onError={(e) => {
                                    console.error('Failed to load image:', mediaUrl);
                                    e.target.style.display = 'none';
                                  }}
                                  loading="lazy"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <XCircle className="w-7 h-7 text-red-600" />
                Cancel Order
              </h3>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Cancellation Fee Information */}
            {(() => {
              const feeInfo = calculateCancellationFee();
              return (
                <div className={`mb-4 p-4 rounded-lg border-2 ${
                  feeInfo.refundPercentage === 100 ? 'bg-green-50 border-green-200' :
                  feeInfo.refundPercentage === 70 ? 'bg-orange-50 border-orange-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <p className={`font-semibold mb-2 ${
                    feeInfo.refundPercentage === 100 ? 'text-green-800' :
                    feeInfo.refundPercentage === 70 ? 'text-orange-800' :
                    'text-red-800'
                  }`}>
                    {feeInfo.message}
                  </p>

                  {job.totalAmount && (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Order Amount:</span>
                        <span className="font-medium">${job.totalAmount.toFixed(2)}</span>
                      </div>
                      {feeInfo.fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">Cancellation Fee (30%):</span>
                          <span className="font-medium text-red-600">-${feeInfo.fee.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-700">Platform Fee:</span>
                        <span className="font-medium text-red-600">-$5.00</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                        <span className="font-semibold text-gray-900">Refund Amount:</span>
                        <span className="font-bold text-green-600">
                          ${((job.totalAmount || 0) - feeInfo.fee - 5).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {!feeInfo.canCancel && (
                    <p className="text-sm text-red-700 mt-2">
                      ⚠️ Cannot cancel - less than 24 hours before scheduled service
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Cancellation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Please provide a reason for cancelling this order..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                rows="4"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Once cancelled, this action cannot be undone. Refunds will be processed within 5-7 business days.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Keep Order
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={cancelling || !cancelReason.trim() || !calculateCancellationFee().canCancel}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default JobDetailsPage;